import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store'; // Adjust the import path as needed
import { emailSettingsEndpoints } from '../../services/api/endpoints/email-settings.endpoints';
import { EmailSettings } from '../../types/email-settings';

interface EmailSettingsState {
  emailSettings: EmailSettings | null; // Single object for E-Mail settings
  isLoading: boolean;
  error: string | null;
}

const initialState: EmailSettingsState = {
  emailSettings: null, // Initialize as null
  isLoading: false,
  error: null,
};

// Async Thunk to fetch E-Mail settings
export const fetchEmailSettings = createAsyncThunk(
  'emailSettings/fetchEmailSettings',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await emailSettingsEndpoints.getEmailSettings(organizationId);
      return response; // Return the fetched E-Mail settings
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch E-Mail settings');
    }
  }
);

// Async Thunk to create or update E-Mail settings
export const saveEmailSettings = createAsyncThunk(
  'emailSettings/saveEmailSettings',
  async (
    { organizationId, emailSettings }: { organizationId: string; emailSettings: EmailSettings },
    { rejectWithValue }
  ) => {
    try {
      const response = await emailSettingsEndpoints.createEmailSettings(
        organizationId,
        emailSettings
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to save E-Mail settings');
    }
  }
);

export const updateEmailSettings = createAsyncThunk(
  'emailSettings/updateEmailSettings',
  async (
    { organizationId, emailSettings }: { organizationId: string; emailSettings: EmailSettings },
    { rejectWithValue }
  ) => {
    try {
      const response = await emailSettingsEndpoints.updateEmailSettings(
        organizationId,
        emailSettings
      );
      return response; // Return the updated E-Mail settings
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update E-Mail settings');
    }
  }
);

// Async Thunk to delete E-Mail settings
export const deleteEmailSettings = createAsyncThunk(
  'emailSettings/deleteEmailSettings',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      await emailSettingsEndpoints.deleteEmailSettings(organizationId);
      return null; // Return null to indicate deletion
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to delete E-Mail settings');
    }
  }
);

const emailSettingsSlice = createSlice({
  name: 'emailSettings',
  initialState,
  reducers: {
    clearEmailSettings(state) {
      state.emailSettings = null;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch E-Mail settings
      .addCase(fetchEmailSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmailSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.emailSettings = action.payload; // Store the fetched E-Mail settings
      })
      .addCase(fetchEmailSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Save E-Mail settings (create or update)
      .addCase(saveEmailSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveEmailSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.emailSettings = action.payload; // Store the saved E-Mail settings
      })
      .addCase(saveEmailSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update E-Mail settings
      .addCase(updateEmailSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateEmailSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.emailSettings = action.payload; // Store the updated E-Mail settings
      })
      .addCase(updateEmailSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete E-Mail settings
      .addCase(deleteEmailSettings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteEmailSettings.fulfilled, state => {
        state.isLoading = false;
        state.emailSettings = null; // Clear E-Mail settings after deletion
      })
      .addCase(deleteEmailSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearEmailSettings } = emailSettingsSlice.actions;
export const selectEmailSettings = (state: RootState) => state.emailSettings;
export default emailSettingsSlice.reducer;
